import { useState, useRef, useEffect, MouseEvent } from 'react';

import isMobileAgent from 'utils/isMobileAgent';

import Menu, { MenuProps } from 'components/controls/menu';
import MenuItem from '@material-ui/core/MenuItem';

import { useStyles } from './style';

export interface CaseMenuOption {
  id: string | number;
  label: string;
  children?: CaseMenuOption[];
  visible?: boolean;
  action?: () => void;
}

export interface CaseMenuProps extends MenuProps {
  options: CaseMenuOption[];
  sub?: boolean;
}

const CaseMenu: React.FC<CaseMenuProps> = ({
  disabledPortal = false,
  options,
  sub = false,
  ...rest
}) => {
  const classes = useStyles();

  const itemRef = useRef<HTMLLIElement | null>(null);

  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  useEffect(() => {
    if (!rest.open) {
      itemRef.current = null;
      setSelectedId(null);
    }
  }, [rest.open]);

  const handleToggleSubMenu = (
    e: MouseEvent<HTMLLIElement>,
    id: string | number
  ) => {
    e.stopPropagation();

    setSelectedId((prevId) => {
      if (prevId === id) {
        itemRef.current = null;
        return null;
      }

      itemRef.current = e.currentTarget;
      return id;
    });
  };

  const handleMouseEnterMenuItem = (
    e: MouseEvent<HTMLLIElement>,
    id: string | number
  ) => {
    e.stopPropagation();

    itemRef.current = e.currentTarget;
    setSelectedId(id);
  };

  const handleMouseLeaveMenuItem = (e: MouseEvent<HTMLLIElement>) => {
    itemRef.current = null;
    setSelectedId(null);
  };

  const handleCloseSubMenu = () => {
    itemRef.current = null;
    setSelectedId(null);
  };

  const isMobile = isMobileAgent();

  return (
    <Menu
      disabledPortal={disabledPortal}
      PopperProps={{
        placement: 'bottom-end',
      }}
      PaperProps={{
        classes: {
          elevation1: classes.elevation1,
          rounded: classes.rounded,
        },
      }}
      MenuListClasses={{
        root: classes.menuList,
      }}
      {...rest}
    >
      {options.map(({ id, label, children, action, visible = true }) => {
        let openSubMenu = selectedId === id;

        if (!visible) return null;

        return (
          <MenuItem
            className={classes.menuItem}
            key={id}
            onClick={action || ((e) => handleToggleSubMenu(e, id))}
            onMouseEnter={
              action || isMobile
                ? undefined
                : (e) => handleMouseEnterMenuItem(e, id)
            }
            onMouseLeave={
              action || isMobile ? undefined : handleMouseLeaveMenuItem
            }
            selected={openSubMenu}
          >
            <span className={classes.menuLabel}>{label}</span>
            {rest.open && children && children.length ? (
              <CaseMenu
                open={openSubMenu}
                anchorEl={itemRef.current}
                options={children}
                handleClose={handleCloseSubMenu}
                PopperProps={{
                  placement: isMobile ? 'left-start' : 'right-start',
                }}
              />
            ) : null}
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default CaseMenu;
